<template>
  <div id="app" v-if="isReadyToShow" @click="onBodyClick($event)">
    <error></error>
    <Navigation v-if="showNavigation" />
    <div
      v-if="isRouterReady"
      id="content"
      class="overflow-hidden print:overflow-visible"
      :class="{
        'tablet:pl-[5rem] desktop:pl-[13.625rem] print:pl-0': showNavigation,
      }"
    >
      <router-view v-slot="{ Component }">
        <ViewSpinner :component="Component" />
      </router-view>
    </div>
    <Confirmation />
    <SimpleError />
  </div>

  <div
    v-else-if="
      isLoaded &&
      isLoginSucceed &&
      authStore.isAuthenticated &&
      !authStore.privacyPolicyAccepted
    "
  >
    <PrivacyPolicy />
  </div>

  <LoadingDialog v-else />
  <NotifyContainer />
  <UnsavedChanges />
  <LogoutPrompt />
</template>

<script lang="ts" setup>
import * as Sentry from "@sentry/vue";
import Error from "@/components/generic/Error.vue";
import Navigation from "@/components/layouts/Navigation.vue";
import ViewSpinner from "@/components/layouts/ViewSpinner.vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth.module";
import Confirmation from "@/stories/displays/Confirmation/Confirmation.vue";
import SimpleError from "@/stories/displays/SimpleError/SimpleError.vue";
import { useI18n } from "vue-i18n";
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import emitter from "@/emitter";
import i18n from "@/i18n";
import { ALLOWED_IFRAME_ORIGINS, IS_IBG_MANAGER } from "@/consts";
import { validationSetup } from "@/helpers/forms/validate";
import { useHead } from "@unhead/vue";
import type { CustomMeta } from "@/interfaces";
import type { SupportedLocales } from "@/types";
import { setStores } from "@/stores";
import PrivacyPolicy from "@/views/PrivacyPolicy.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import NotifyContainer from "@/stories/displays/NotifyContainer/NotifyContainer.vue";
import { IFrameMessage } from "@/enums";
import sendMessageToParentWindow from "@/helpers/routes/sendMessageToParentWindow";
import { useRouteGroupMeta } from "@/composables/useRouteGroupMeta";
import { useMatomo } from "@/composables/useMatomo";
import iframe from "@/helpers/routes/iframe";
import UnsavedChanges from "@/stories/displays/UnsavedChanges/UnsavedChanges.vue";
import LogoutPrompt from "@/stories/displays/LogoutPrompt/LogoutPrompt.vue";

useMatomo();
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const meta = useRouteGroupMeta<CustomMeta | null>();
const head = useHead();
const isRouterReady = ref(false);
const { t } = useI18n();

const isLoginSucceed = ref(false);

window.$route = route;

// This function loads the validation translations.
validationSetup();

// Auto-update the session storage
authStore.$subscribe(authStore.updateToStorage);

const root = document.querySelector(":root") as HTMLElement | null;
const setQuillTranslation = (key: string, translationKey: string) => {
  if (root) {
    root.style.setProperty(`--quill-text-${key}`, `'${t(translationKey)}'`);
  }
};

const onLoginSuccess = () => {
  isLoginSucceed.value = true;
  // We should set the stores map. Check the function details
  setStores();
};

const setSearchQueryParamsForStores = async () => {
  // Router should be ready first.
  await router.isReady();

  isRouterReady.value = true;
};

onMounted(() => {
  // Some of the stores make API requests. Those requests should be sent after
  // the login. That's why we should wait.
  authStore.addLoginListener(onLoginSuccess);

  setSearchQueryParamsForStores();
});

onBeforeUnmount(() => {
  authStore.removeLoginListener(onLoginSuccess);
});

// We have to set Quill translation keys as CSS variable because of Quill
// use the texts via CSS directly.
// https://github.com/slab/quill/issues/4331
setQuillTranslation("visit-url", "quill.visitURL");
setQuillTranslation("edit", "global.edit");
setQuillTranslation("remove", "global.remove");
setQuillTranslation("enter-link", "quill.enterLink");
setQuillTranslation("save", "global.save");

const isLoaded = ref(false);

const isReadyToShow = computed(() => {
  // The app is not initialized yet
  if (isLoaded.value === false) {
    return false;
  }

  // If this is an iframe, we don't need to wait for the authStore/login
  if (window.$features.iframe) {
    return true;
  }

  // The callback view should be worked all the time
  if (
    route.name === "callback" ||
    route.name === "relativeProfileCreation" ||
    route.name === "loginError"
  ) {
    return true;
  }

  if (!authStore.privacyPolicyAccepted) {
    return false;
  }

  // We should wait to show the views for the login.
  return isLoginSucceed.value;
});

const locale = computed(() => {
  return authStore.institutionLocale || "da";
});

const showNavigation = computed(() => {
  return (
    isLoaded.value && // no blink effect
    !window.$features.iframe && // Iframe means this is the planner. No navigation on planner
    route.name !== undefined && // no blink effect
    route.name !== "relativeProfileCreation" // No navigation on relative-profile-creation view
  );
});

const onBodyClick = (event: Event) => {
  emitter.emit("bodyClick", event);
};

const initApp = async () => {
  // Init actions of the auth store.
  await authStore.init();

  i18n.global.locale.value = locale.value as SupportedLocales;

  Sentry.setUser({
    id: `${authStore.profile?.id}`,
    username: `${authStore.profileType}-${authStore.profile?.id}`,
  });

  if (window.$features.iframe) {
    if (IS_IBG_MANAGER) {
      // If this is an iframed content (old/core manager is the parent), we should ask
      // for the authentication information first. We should wait the auth info.
      // We can not acceept the app is ready to use before we got auth information
      // Otherwise composable and stores would try to fetch data (like planner
      // filters, categories etc) before we have a valid authentication.
      sendMessageToParentWindow(IFrameMessage.PingAuthenticationInfo);
      window.addEventListener("message", oldManagerMessageListener);
    } else {
      isLoaded.value = true;
    }
  } else {
    isLoaded.value = true;
  }
};

const oldManagerMessageListener = async (event: MessageEvent) => {
  const { origin, data } = event;

  // We can NOT trust all origins.
  // Do we trust the sender of this message?
  if (ALLOWED_IFRAME_ORIGINS.includes(origin) === false) {
    console.warn("Untrusted origin:", origin);
    return;
  }

  // We should set the authentication information
  if (data.message === IFrameMessage.PongAuthenticationInfo) {
    // We should set which app (core/old) iframed the new manager.
    iframe.setIframeType(event.origin);

    // The CORE Manager is also using the vue-manager project. But it doesn't
    // have an institution. It should share the token information to make the
    // vue-manager ready. But we don't need it actually to set the authStore.
    // We should only set old manager login, if the parent is the old manager.
    if (iframe.getIframeType("old")) {
      await authStore.setOldManagerLogin(data.token, data.institutionId);
    }

    // Now we can say the app is ready to use.
    isLoaded.value = true;
    console.log("✅ The iframed auth is completed!");
  } else {
    console.warn(`Undefined message: ${data.message}`);
  }
};

onBeforeMount(() => {
  initApp();
});

onBeforeUnmount(() => {
  if (IS_IBG_MANAGER && window.$features.iframe) {
    window.removeEventListener("message", oldManagerMessageListener);
  }
});

watch(
  () => route.name,
  () => {
    if (meta.value) {
      const pageTitle = IS_IBG_MANAGER
        ? t("global.title_ibg")
        : t("global.title_bp");

      head.patch({ title: `${t(meta.value.displayName)} - ${pageTitle}` });
    }
  },
);
</script>

<style lang="scss">
@use "tailwindcss/base" as *;
@use "tailwindcss/components" as *;
@use "tailwindcss/utilities" as *;

@font-face {
  font-family: "IBG";
  src: url("./assets/fonts/ibgv2.eot");
  src:
    url("./assets/fonts/ibgv2.eot?#iefix") format("embedded-opentype"),
    url("./assets/fonts/ibgv2.woff") format("woff"),
    url("./assets/fonts/ibgv2.ttf") format("truetype"),
    url("./assets/fonts/ibgv2.svg") format("svg");
  font-weight: normal;
  font-style: normal;
}

.font-ibg {
  font-family: "IBG";
  font-size: 2rem;
  font-weight: normal;
  font-style: normal;

  &.activity::before {
    content: "a";
    font-size: 2rem;
    font-weight: normal;
    font-style: normal;
  }
}

body {
  font-family: "Arial", "Helvetica", sans-serif;
  font-size: 0.9375rem;
}

/**
* Remove outline etc
*/
button {
  -webkit-appearance: none;
  appearance: none;
  font-weight: bold;
}
button::-moz-focus-inner {
  border: 0;
}
.tooltip {
  background: #000;
  color: #fff;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  max-width: 9.375rem;
  text-align: center;
}

select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3e%3cpath d='M7 7l3-3 3 3m0 6l-3 3-3-3' stroke='%239fa6b2' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  font-size: 1rem;
  line-height: 1.5;
  background-position: right 0.5rem center;
  background-size: 1.5em 1.5em;
  padding-right: 1.875rem !important;
}

.form-checkbox {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  display: inline-block;
  vertical-align: middle;
  background-origin: border-box;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -ms-flex-negative: 0;
  flex-shrink: 0;
  height: 1rem;
  width: 1rem;
  color: #3f83f8;
  background-color: #ffffff;
  border-color: #d2d6dc;
  border-width: 1px;
  border-radius: 0.25rem;
}

.form-checkbox:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M5.707 7.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4a1 1 0 0 0-1.414-1.414L7 8.586 5.707 7.293z'/%3e%3c/svg%3e");
  border-color: transparent;
  background-color: currentColor;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
}

.drop-down button {
  display: block;
  background-color: white;
  color: #696969;
}
.drop-down button.selected {
  color: white;
  background-color: #5f7289;
}

.drop-down button {
  border-radius: 0.375rem;
  border: 1px #d9d9d9 solid;
  padding: 0.5rem 1.25rem;
}
</style>
